/**
 * The code mirror editor wrapper.
 * Use initCodeFromCodeAtom to render the codemirror instance with whatever is in the code atom.
 * @module Editor
 * @category Display Components
 */

import { lightTheme, measures } from "@quickpoint.me/shared";

import CodeMirror from "./CodeMirror";
import CodeMirrorRecoilStateController from "./CodeMirrorRecoilStateController";
import EditorBar from "./EditorBar";
import { IsInPortrait } from "../../state/selectors";
import { StackCfKeyAtom } from "../../state/stackAtoms";
import { ShowTour } from "../../state/uiAtoms";
import { AiChatLevelAtom } from "../../state/aiChatAtoms";
import AiChatPanel from "../chat/AiChatPanel";
import { useSupportChat } from "../../hooks/useSupportChat";
import { nanoid } from "nanoid";
import { useEffect } from "react";
import styled from "styled-components";
import { useRecoilState, useRecoilValue } from "recoil";

const StyledEditor = styled.main<{ $isInPortrait: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${(props) => (props.$isInPortrait ? measures.editor.portrait.width : measures.editor.landscape.width)};
  height: 100%;
  min-width: 0;
  overflow: hidden;
  grid-area: editor;
  background-color: ${lightTheme.backgroundColor};
`;

const EditorContentArea = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

function Editor() {
	useSupportChat();
	const isInPortrait = useRecoilValue(IsInPortrait);
	const stackCfKey = useRecoilValue(StackCfKeyAtom);
	const [chatLevel, setChatLevel] = useRecoilState(AiChatLevelAtom);
	const showTour = useRecoilValue(ShowTour);

	// Ctrl+Shift+A toggles between chat (Level 1) and code editor (Level 3)
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.ctrlKey && e.shiftKey && e.key === "A") {
				e.preventDefault();
				setChatLevel((prev) => (prev === 3 ? 1 : 3));
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [setChatLevel]);

	// Focus routing on level change — suppressed mid-tour: the scripted demos
	// need no caret, and focusing pops the mobile keyboard over the tour card,
	// shifting its arrows out from under the user's tap
	useEffect(() => {
		if (showTour) return;
		if (chatLevel === 3) {
			const cm = document.querySelector(".cm-content") as HTMLElement | null;
			cm?.focus();
		} else if (chatLevel === 2) {
			const editor = document.getElementById("element-editor");
			editor?.focus();
		} else if (chatLevel === 1) {
			requestAnimationFrame(() => {
				const cm = document.querySelector(".chat-codemirror .cm-content") as HTMLElement | null;
				cm?.focus();
			});
		}
	}, [chatLevel, showTour]);

	return (
		<>
			<StyledEditor id="main-editor" className="qp-editor" $isInPortrait={isInPortrait}>
				<EditorBar />

				<EditorContentArea>
					<div style={{ visibility: chatLevel < 3 ? "hidden" : "visible", height: "100%" }}>
						<CodeMirror
							isInPortrait={isInPortrait}
							key={stackCfKey + nanoid()}
							stackCfKey={stackCfKey}
						/>
					</div>
					{chatLevel < 3 && <AiChatPanel />}
				</EditorContentArea>
				<CodeMirrorRecoilStateController />
			</StyledEditor>
		</>
	);
}

export default Editor;
